import { useCallback, useEffect, useRef, useState } from 'react';
import { IconCamera, IconSquare, IconVideo, IconX } from '../icons';
import { useTranslate } from '../i18n/react';
import { Alert } from './Field';
import { Button, IconButton } from './Button';
import {
  CAPTURE_CONSTRAINTS,
  VIDEO_BITS_PER_SECOND,
  cameraErrorKey,
  pickRecordingType,
} from './camera';

export { canOpenCamera } from './camera';

export interface CameraCaptureProps {
  mode: 'photo' | 'video';
  /** For the file name, so the stored object says which question it belongs to. */
  code: string;
  onCaptured: (file: File) => void;
  onClose: () => void;
}

/**
 * The camera, opened in the page rather than handed to the phone's own app.
 *
 * <p>The `capture` attribute on a file input opens the native camera, which takes a better
 * photograph than this does and is the interface the auditor already knows. It has two
 * faults that matter more: a desktop browser ignores it completely, so the button labelled
 * "take a photo" opened a file dialog on a laptop and said nothing about why; and it offers
 * no say over resolution, so a phone set to 4K fills the size limit in fifteen seconds.</p>
 *
 * <p>Here both are answerable. The stream is capped at 1080p before a frame is taken, and a
 * laptop with a webcam does what the button says.</p>
 */
export function CameraCapture({ mode, code, onCaptured, onClose }: CameraCaptureProps) {
  const t = useTranslate();

  const preview = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [ready, setReady] = useState(false);

  /**
   * Stops every track.
   *
   * <p>Not optional housekeeping: a track left running keeps the camera light on after the
   * panel has gone, which reads to anybody watching as the application filming them.</p>
   */
  const release = useCallback(() => {
    recorder.current?.state === 'recording' && recorder.current.stop();
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    navigator.mediaDevices
      .getUserMedia(mode === 'video' ? CAPTURE_CONSTRAINTS : { ...CAPTURE_CONSTRAINTS, audio: false })
      .then((opened) => {
        if (cancelled) {
          opened.getTracks().forEach((track) => track.stop());
          return;
        }

        stream.current = opened;
        if (preview.current) preview.current.srcObject = opened;
        setReady(true);
      })
      .catch((e: unknown) => {
        // Which sentence, decided in camera.ts where it can be asserted; which words, here,
        // because only this side knows the language.
        const key = cameraErrorKey(e instanceof Error ? e.name : '');
        setError(
          key === 'audit.camera.denied'
            ? t('audit.camera.denied')
            : key === 'audit.camera.none'
              ? t('audit.camera.none')
              : t('audit.camera.failed'),
        );
      });

    return () => {
      cancelled = true;
      release();
    };
  }, [mode, release, t]);

  // The length, because a recording with no clock on it is one nobody knows the size of
  // until it is too large to send.
  useEffect(() => {
    if (!recording) return;

    const tick = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(tick);
  }, [recording]);

  const finish = (blob: Blob, extension: string, type: string) => {
    onCaptured(new File([blob], `${code || 'kanit'}-${Date.now()}.${extension}`, { type }));
    onClose();
  };

  const takePhoto = () => {
    const video = preview.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);

    // JPEG at 0.92: a 1080p frame lands around half a megabyte, against fifteen allowed.
    canvas.toBlob(
      (blob) => blob && finish(blob, 'jpg', 'image/jpeg'),
      'image/jpeg',
      0.92,
    );
  };

  const startRecording = () => {
    if (!stream.current) return;

    const type = pickRecordingType((t) => MediaRecorder.isTypeSupported(t));
    chunks.current = [];
    setSeconds(0);

    const made = new MediaRecorder(stream.current, {
      mimeType: type || undefined,
      videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
    });

    made.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
    made.onstop = () => {
      const blob = new Blob(chunks.current, { type: made.mimeType });
      finish(blob, made.mimeType.includes('mp4') ? 'mp4' : 'webm', made.mimeType);
    };

    recorder.current = made;
    made.start();
    setRecording(true);
  };

  const stopRecording = () => {
    setRecording(false);
    recorder.current?.stop();
  };

  return (
    <div
      className="nv-camera"
      role="dialog"
      aria-modal="true"
      aria-label={mode === 'video' ? t('audit.takeVideo') : t('audit.takePhoto')}
    >
      <div className="nv-camera__bar">
        <IconButton label={t('common.close')} icon={IconX} onClick={onClose} />
        {recording ? (
          <span className="nv-camera__clock">
            {`${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`}
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="nv-camera__message">
          <Alert>{error}</Alert>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </div>
      ) : (
        <>
          {/* The box decides the size, not the stream: see .nv-camera__stage. */}
          <div className="nv-camera__stage">
            {/* Muted, or the microphone comes back out of the speaker as a howl. */}
            <video ref={preview} className="nv-camera__preview" autoPlay playsInline muted />
          </div>

          <div className="nv-camera__controls">
            {mode === 'photo' ? (
              <Button size="lg" icon={IconCamera} disabled={!ready} onClick={takePhoto}>
                {t('audit.takePhoto')}
              </Button>
            ) : recording ? (
              <Button size="lg" variant="danger" icon={IconSquare} onClick={stopRecording}>
                {t('audit.stopRecording')}
              </Button>
            ) : (
              <Button size="lg" icon={IconVideo} disabled={!ready} onClick={startRecording}>
                {t('audit.startRecording')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
